import React, { useCallback, useState } from 'react';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { mc } from '@shakkerai/report';
import { Tooltip } from '@mantine/core';
import flashIcon from '@shakkerai/pro-components/src/button/flash.svg';
import { metaReport } from '@shakkerai/util-meta-report';
import { useVipState, updateVipState } from '@shakkerai/pro-store';
import { useLoadings } from '@shakkerai/pro-store';
import { varyImage } from '@shakkerai/pro-utils';
import { applyReferenceImage } from '@starflow/app/modules/generator/helpers';
import {
  applyImg2ImgSourceImage,
  updatePromptFromImage,
  applyGenerateParams,
} from '@starflow/app/modules/generator/helpers';
import { useElementGenInfo } from '@shakkerai/pro-hooks';
import { MODEL_SD35 } from '@shakkerai/pro-models';
import { useVaryCredits } from '@shakkerai/pro-commercial';
import { setTempState, useProjectState, useUnitData } from '@starflow/app/store';
import { applyColorReferenceFromImg } from '@starflow/app/modules/generator/helpers';
import { getActiveGeneratorNodeId } from '@starflow/app/helpers/flow';
import classNames from 'classnames';
import { controlNetList } from '@shakkerai/pro-constants';

const OverallVaryButton = React.memo((props) => {
  const { loading, element, isExternalImage, isWorkflowImage } = props;
  const count = useUnitData(null, 'count');
  const { showPowerFlag } = useVipState();
  const { credits, insfficient } = useVaryCredits(element, count);

  const elementId = element.id;
  const innerImageId = element.innerImageId;

  const handleRemix = useCallback(() => {
    mc('tool.canvas.entire_variation', { image_id: innerImageId });

    if (!insfficient) {
      varyImage(elementId, { isOverallVary: true });
    } else {
      // 算力不足的情况
      if (showPowerFlag) {
        // 普通会员，算力不足
        // 集成付费弹窗逻辑
        updateVipState({
          proPurchaseModalVisible: true,
        });
      } else {
        varyImage(elementId, { isOverallVary: true });
      }
    }

    metaReport(`generateBtn`);
  }, [insfficient, showPowerFlag, elementId, innerImageId]);

  const showCredits = showPowerFlag && credits >= 0;

  if (isExternalImage) {
    return (
      <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_tool_use_style_disabled_tip')}>
        <button disabled className="image-tool-btn">
          {translate('image_tool_vary_overall')}
        </button>
      </Tooltip>
    );
  }

  const tooltip = (
    <div className="flex items-center">
      <span>{translate('image_tool_vary_overall_tip')}</span>
      {showCredits && (
        <>
          <img src={flashIcon} className="ml-1 -translate-y-px" width={14} height={14} alt="" />
          <span>{credits}</span>
        </>
      )}
    </div>
  );

  return (
    <Tooltip withArrow position="bottom" className="scale-90" label={tooltip}>
      <button disabled={loading || isWorkflowImage} onClick={handleRemix} className="image-tool-btn">
        {translate('image_tool_vary_overall')}
      </button>
    </Tooltip>
  );
});

const DetailsVaryButton = React.memo((props) => {
  const { loading, element, isExternalImage, isWorkflowImage } = props;
  const count = useUnitData(null, 'count');
  const { showPowerFlag } = useVipState();
  const { credits, insfficient } = useVaryCredits(element, count);

  const elementId = element.id;
  const innerImageId = element.innerImageId;

  const handleRemix = useCallback(() => {
    mc('tool.canvas.detail_variation', { image_id: innerImageId });

    if (!insfficient) {
      varyImage(elementId);
    } else {
      // 算力不足的情况
      if (showPowerFlag) {
        // 普通会员，算力不足
        // 集成付费弹窗逻辑
        updateVipState({
          proPurchaseModalVisible: true,
        });
      } else {
        varyImage(elementId);
      }
    }

    metaReport(`generateBtn`);
  }, [insfficient, showPowerFlag, elementId, innerImageId]);

  const showCredits = showPowerFlag && credits >= 0;

  if (isExternalImage) {
    return (
      <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_tool_use_style_disabled_tip')}>
        <button disabled className="image-tool-btn">
          {translate('image_tool_vary_details')}
        </button>
      </Tooltip>
    );
  }

  const tooltip = (
    <div className="flex items-center">
      <span>{translate('image_tool_vary_details_tip')}</span>
      {showCredits && (
        <>
          <img src={flashIcon} className="ml-1 -translate-y-px" width={14} height={14} alt="" />
          <span>{credits}</span>
        </>
      )}
    </div>
  );

  return (
    <Tooltip withArrow position="bottom" className="scale-90" label={tooltip}>
      <button disabled={loading || isWorkflowImage} onClick={handleRemix} className="image-tool-btn">
        {translate('image_tool_vary_details')}
      </button>
    </Tooltip>
  );
});

const UseStyleButton = React.memo((props) => {
  const { isExternalImage, hasLoraModels, onClick, isHiddenStrategyImage, showStyleSentTip } = props;

  return isExternalImage ? (
    <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_tool_use_style_disabled_tip')}>
      <button disabled onClick={onClick} className={`image-tool-btn`}>
        {translate('image_tool_use_lora')}
      </button>
    </Tooltip>
  ) : isHiddenStrategyImage ? (
    <Tooltip
      withArrow
      position="bottom"
      className="scale-90"
      label={translate(showStyleSentTip ? 'img_style_sent' : 'image_tool_use_style_tip')}
    >
      <button onClick={onClick} className={`image-tool-btn`}>
        {translate('image_tool_use_style')}
      </button>
    </Tooltip>
  ) : (
    <Tooltip
      withArrow
      position="bottom"
      className="scale-90"
      disabled={hasLoraModels}
      label={translate('image_tool_use_lora_disabled_tip')}
    >
      <button disabled={!hasLoraModels} onClick={onClick} className={`image-tool-btn`}>
        {translate('image_tool_use_lora')}
      </button>
    </Tooltip>
  );
});

export const ImageSubTools = React.memo(({ element }) => {
  const { baseType, presetBaseModelId } = useUnitData();
  const promptMatching = useLoadings('fetch-prompt-' + element.id);
  const preparing = useLoadings('preparing-' + element.id);
  const [showStyleSentTip, setShowStyleSentTip] = useState(false);
  const genInfo = useElementGenInfo(element);

  const handleMatchPrompt = useCallback(() => {
    mc('tool.canvas.copy_prompt', { image_id: element.innerImageId });
    updatePromptFromImage(element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element]);

  const handleSendToReference = useCallback(() => {
    mc('tool.canvas.reference', { image_id: element.innerImageId });
    applyReferenceImage(element);
  }, [element]);

  const handleSendToColorReference = useCallback(() => {
    applyColorReferenceFromImg(element);
  }, [element]);

  const handleUseStyle = useCallback(() => {
    mc('tool.canvas.copy_match', { image_id: element.innerImageId });
    setShowStyleSentTip(true);
    applyGenerateParams(
      genInfo,
      {
        styleIcon: element.url,
      },
      'style',
    );

    setTimeout(() => {
      setShowStyleSentTip(false);
    }, 3000);
  }, [element.url, genInfo, element.innerImageId]);

  const handleSendToImg2Img = useCallback(
    (event) => {
      const generatorId = getActiveGeneratorNodeId();
      mc('tool.canvas.img2img', { image_id: element.innerImageId });
      applyImg2ImgSourceImage(element, generatorId);
      setTempState('exclusiveWindow', `image-input-options-${generatorId}`);

      event.stopPropagation();
    },
    [element],
  );

  // SD35模型不支持作为参考图
  const disableReference = baseType === MODEL_SD35;
  // 仅支持星流
  const disableColorReference = presetBaseModelId !== 'base_checkpoint';

  const isExternalImage = !element?.innerTaskId;
  const isWorkflowImage = genInfo?.generateType === 'workflow';
  const hasLoraModels = genInfo?.loraModels?.length > 0;
  const isHiddenStrategyImage = genInfo?.loraModels?.some((item) => item.isHiddenStrategy);

  // cn选择主体后，图生图不可用
  const currentNodeId = useProjectState('currentNodeId');
  const generator = useProjectState((state) => state.nodeMap[currentNodeId])?.data;
  const currentCnConflictWithImg2img = !!generator?.data?.controlnets?.some((cn) => {
    const cnInfo = controlNetList.find((item) => item.value === cn.type);
    return cnInfo && cnInfo.img2imgUnsupport;
  });

  const disabledImg2img = currentCnConflictWithImg2img;

  return (
    <div className="absolute z-1 left-1/2 -translate-x-1/2 -bottom-10 w-auto flex items-center whitespace-nowrap text-xs">
      <div className="flex items-center pl-2 py-2 bg-white shadow-light rounded-sm">
        <span className="mr-2 text-[#2c2c2c] font-semibold">{translate('image_tool_adjust')}</span>
        <DetailsVaryButton
          isExternalImage={isExternalImage}
          isWorkflowImage={isWorkflowImage}
          loading={preparing}
          element={element}
        />
        <OverallVaryButton
          isExternalImage={isExternalImage}
          isWorkflowImage={isWorkflowImage}
          loading={preparing}
          element={element}
        />
        <div className="w-px h-5 ml-1 mr-3 bg-[#d9d9d9]" />
        <span className="mr-2 text-[#2c2c2c] font-semibold">{translate('image_tool_use')}</span>
        <UseStyleButton
          isExternalImage={isExternalImage}
          isHiddenStrategyImage={isHiddenStrategyImage}
          showStyleSentTip={showStyleSentTip}
          hasLoraModels={hasLoraModels}
          onClick={handleUseStyle}
        />
        <button disabled={promptMatching} onClick={handleMatchPrompt} className="image-tool-btn">
          {translate('image_tool_use_prompt')}
        </button>
        <div className={`group relative image-tool-btn`}>
          {translate('image_tool_use_image')}
          <Icon className="shk-kuangjia-xiala -mr-2" color="#666" fontSize={18} />
          <div className="absolute hidden group-hover:block left-0 top-[24px] flex flex-col items-stretch rounded-sm bg-[#2c2c2c] py-1.5 px-0">
            <button
              disabled={disabledImg2img}
              onClick={handleSendToImg2Img}
              className={classNames(
                'flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c]',
                disabledImg2img ? 'opacity-60 cursor-not-allowed' : '',
              )}
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('img_use_as_i2i_title')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('img_use_as_i2i_desc')}</span>
            </button>
            <button
              disabled={disableReference}
              onClick={handleSendToReference}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c] disabled:pointer-events-none disabled:opacity-30"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('img_use_as_ref_title')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('img_use_as_ref_desc')}</span>
            </button>
            <button
              disabled={disableColorReference}
              onClick={handleSendToColorReference}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c] disabled:pointer-events-none disabled:opacity-30"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('color_canvas_para1')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('color_canvas_para2')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
